import * as React from 'react';

import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import EnterpriseOnboardingContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseOnboardingContext';
import { getEnterpriseLearningGoalCopy } from 'bundles/page-config-common/utils/getEnterpriseLearningGoalCopy';
import { DEFAULT_SKILLS } from 'bundles/program-personalized-tab/constants';
import EnterpriseLearnerOnboarding from 'bundles/program-personalized-tab/queries/EnterpriseLearnerOnboarding.graphql';
import type {
  EnterpriseLearningOnboardingQuery as EnterpriseLearnerOnboardingQueryType,
  EnterpriseLearningOnboardingQueryVariables,
} from 'bundles/program-personalized-tab/queries/__generated__/EnterpriseLearnerOnboarding';

/**
 * @description This provider is used to fetch the enterprise onboarding data for the learner.
 */
const EnterpriseOnboardingProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const userId = user.get().id;

  const { data, loading } = useQuery<EnterpriseLearnerOnboardingQueryType, EnterpriseLearningOnboardingQueryVariables>(
    EnterpriseLearnerOnboarding,
    {
      variables: { userId: userId?.toString() },
      context: { clientName: 'gatewayGql' },
      skip: !userId,
    }
  );

  if (loading) {
    return (
      <EnterpriseOnboardingContext.Provider
        value={{
          enterpriseOnboardingDataLoading: true,
          enterpriseLearningGoalMessage: undefined,
          enterpriseCompletedOnboarding: false,
          skills: [],
        }}
      >
        {children}
      </EnterpriseOnboardingContext.Provider>
    );
  }

  const hasCompletedOnboarding = data?.LearnerOnboarding?.queryResponseByUserId?.isCompleted ?? false;
  const onboardingRoleId = data?.LearnerOnboarding?.queryResponseByUserId?.occupationId;
  const onboardingGoals = data?.LearnerOnboarding?.queryResponseByUserId?.goals;
  const isUnsureOfRole = Boolean(data?.LearnerOnboarding?.queryResponseByUserId?.isUnsureOfRole);
  const selectedRoleSkills = data?.LearnerOnboarding?.queryResponseByUserId.occupationSkills ?? [];
  const skills = selectedRoleSkills.length ? selectedRoleSkills : DEFAULT_SKILLS;

  const enterpriseLearningGoalMessage = userId
    ? getEnterpriseLearningGoalCopy(hasCompletedOnboarding, onboardingRoleId, onboardingGoals, isUnsureOfRole)
    : undefined;

  return (
    <EnterpriseOnboardingContext.Provider
      value={{
        enterpriseOnboardingDataLoading: false,
        enterpriseLearningGoalMessage,
        enterpriseCompletedOnboarding: hasCompletedOnboarding,
        skills,
      }}
    >
      {children}
    </EnterpriseOnboardingContext.Provider>
  );
};

export default EnterpriseOnboardingProvider;
